import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCollection, PageHeader, CrudActions, Modal, Empty } from "../components/Crud";
import { clientDisplayName } from "../lib/clientName";
import { Plus, Users } from "lucide-react";

export default function Clients() {
    const { t } = useTranslation();
    const { items, create, update, remove } = useCollection("clients");
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [q, setQ] = useState("");
    const blank = { first_name: "", middle_name: "", surname: "", phone: "", email: "",
        address: "", bank_account: "", tax_code: "", passport: "", driving_licence: "",
        resident_number: "", verified: false };
    const [form, setForm] = useState(blank);

    const submit = async (e) => {
        e.preventDefault();
        if (edit) await update(edit.id, form); else await create(form);
        setOpen(false); setEdit(null); setForm(blank);
    };
    const startEdit = (c) => { setEdit(c); setForm({ ...blank, ...c }); setOpen(true); };
    const filtered = items.filter((c) => {
        const s = `${clientDisplayName(c)} ${c.email || ""}`.toLowerCase();
        return s.includes(q.toLowerCase());
    });

    return (
        <div data-testid="clients-page">
            <PageHeader title={t("pages.clientsTitle")} subtitle={t("pages.clientsEyebrow")}
                action={
                    <button onClick={() => { setEdit(null); setForm(blank); setOpen(true); }}
                            data-testid="add-client-btn" className="btn-primary inline-flex items-center gap-2">
                        <Plus size={16} /> {t("pages.clientsAdd")}
                    </button>
                } />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`${t("common.search")}…`}
                   data-testid="clients-search"
                   className="w-full max-w-sm mb-5 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
            {filtered.length === 0 ? (
                <Empty icon={Users} title={t("pages.clientsEmpty")} hint={t("pages.clientsEmptyHint")} />
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map((c) => (
                        <div key={c.id} className={`card-warm p-5 ${c.archived ? "opacity-60" : ""}`} data-testid={`client-card-${c.id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <Link to={`/clients/${c.id}`} className="block flex-1 min-w-0" data-testid={`open-client-${c.id}`}>
                                    <div className="font-serif text-xl truncate" data-testid={`client-name-${c.id}`}>{clientDisplayName(c)}</div>
                                    <div className="text-xs text-[var(--text-ink)]/60 truncate">{c.email || c.phone}</div>
                                </Link>
                                <CrudActions
                                    idBase={`client-${c.id}`}
                                    onEdit={() => startEdit(c)}
                                    onArchive={() => update(c.id, { archived: !c.archived })}
                                    onDelete={() => window.confirm("Delete client?") && remove(c.id)}
                                    archived={c.archived}
                                />
                            </div>
                            {c.address && <div className="text-xs text-[var(--text-ink)]/60 mt-2 line-clamp-2">{c.address}</div>}
                        </div>
                    ))}
                </div>
            )}

            <Modal open={open} onClose={() => { setOpen(false); setEdit(null); }} title={edit ? t("common.edit") : t("pages.clientsAdd")}>
                <form onSubmit={submit} className="grid grid-cols-2 gap-3" data-testid="client-form">
                    {[
                        ["first_name", "First name", true], ["middle_name", "Middle name"], ["surname", "Surname"],
                        ["phone", "Phone"], ["email", "Email"], ["address", "Address"],
                        ["bank_account", "Bank account"], ["tax_code", "Tax code"],
                        ["passport", "Passport"], ["driving_licence", "Driving licence"],
                        ["resident_number", "Resident/NI no."],
                    ].map(([k, lbl, req]) => (
                        <label key={k} className="text-sm">
                            <div className="mb-1">{lbl}{req && " *"}</div>
                            <input value={form[k] || ""} required={!!req}
                                   onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                   data-testid={`client-input-${k}`}
                                   className="w-full px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                        </label>
                    ))}
                    <div className="col-span-2 flex justify-end gap-2 mt-2">
                        <button type="button" onClick={() => setOpen(false)} className="btn-ghost">{t("common.cancel")}</button>
                        <button type="submit" data-testid="client-save-btn" className="btn-primary">{t("common.save")}</button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}
